import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Phone, MapPin, Mail, Facebook, Instagram, Linkedin, Youtube, Building, Award, Shield, Clock } from 'lucide-react';

const Footer = () => {
  const handleWhatsAppClick = () => {
    window.open('https://wa.me/919884611368?text=Hello, I would like to know more about NPSH Developers', '_blank');
  };

  const handlePhoneClick = () => {
    window.open('tel:+919884611368', '_blank');
  };

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Projects', href: '/projects' },
    { name: 'About Us', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Testimonials', href: '/testimonials' },
    { name: 'Contact', href: '/contact' }
  ];

  const services = [
    'Residential Construction',
    'Commercial Development',
    'Interior Design',
    'Project Planning',
    'Construction Management',
    'Maintenance & Support'
  ];

  const projects = [
    'NIVASAM',
    'NARAIN',
    'NITHYAM',
    'Upcoming Projects'
  ];

  const socialMedia = [
    { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/npsh.developers', color: 'hover:text-blue-500' },
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/npsh.developers', color: 'hover:text-pink-500' },
    { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/npsh-developers', color: 'hover:text-blue-600' },
    { name: 'YouTube', icon: Youtube, url: 'https://youtube.com/npsh.developers', color: 'hover:text-red-500' }
  ];

  const certifications = [
    { icon: Award, text: 'RERA Registered' },
    { icon: Shield, text: 'ISO 9001:2015' },
    { icon: Clock, text: '12+ Years Experience' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center mr-4">
                <span className="text-white font-bold text-xl">N</span>
              </div>
              <span className="text-2xl font-bold text-yellow-400">NPSH Developers</span>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6">
              Chennai’s rising construction brand, NPSH Developers Pvt. Ltd., delivering quality residential spaces since 2017 — backed by a passionate team, top-tier architects, and a growing portfolio of completed and ongoing projects across key city locations.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center">
                <MapPin className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0" />
                <span className="text-gray-300 text-sm">Virugambakkam, Chennai - 600092</span>
              </div>
              <div className="flex items-center">
                <Phone className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0" />
                <button
                  onClick={handlePhoneClick}
                  className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-sm"
                >
                  +91 98846 11368
                </button>
              </div>
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0" />
                <div className="flex flex-col">
                  <a
                    href="mailto:md@npshdevelopers.in"
                    className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-sm"
                  >
                    md@npshdevelopers.in
                  </a>
                  <a
                    href="mailto:manager@npshdevelopers.in"
                    className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-sm mt-1"
                  >
                    manager@npshdevelopers.in
                  </a>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <button
              onClick={handleWhatsAppClick}
              className="w-full inline-flex items-center justify-center px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Chat on WhatsApp
            </button>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold text-yellow-400 mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-sm flex items-center group"
                    onClick={e => {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      setTimeout(() => {
                        window.location.href = link.href;
                      }, 400);
                    }}
                  >
                    <span className="w-2 h-2 bg-yellow-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xl font-bold text-yellow-400 mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-sm flex items-center group"
                    onClick={e => {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      setTimeout(() => {
                        window.location.href = '/services';
                      }, 400);
                    }}
                  >
                    <span className="w-2 h-2 bg-yellow-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Projects & Social */}
          <div>
            <h3 className="text-xl font-bold text-yellow-400 mb-6">Featured Projects</h3>
            <ul className="space-y-3 mb-8">
              {projects.map((project) => (
                <li key={project}>
                  <Link
                    to="/projects"
                    className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-sm flex items-center group"
                    onClick={e => {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      setTimeout(() => {
                        window.location.href = '/projects';
                      }, 400);
                    }}
                  >
                    <span className="w-2 h-2 bg-yellow-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                    {project}
                  </Link>
                </li>
              ))}
            </ul>

            
          </div>
        </div>
      </div>

      {/* Certifications Section */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {certifications.map((cert, index) => {
              const IconComponent = cert.icon;
              return (
                <div
                  key={index}
                  className="flex items-center justify-center p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all duration-300"
                >
                  <IconComponent className="h-6 w-6 text-yellow-400 mr-3" />
                  <span className="text-gray-300 text-sm font-medium">{cert.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              <p>&copy; 2025 NPSH Developers. All rights reserved.</p>
            </div>
            <div className="flex flex-wrap items-center space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors duration-200">
                Terms of Service
              </a>
              <span className="text-gray-500">|</span>
              <span className="text-gray-400">
              Made by{' '}
              <a
                href="https://www.linkedin.com/in/sajivjess"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-yellow-400 transition-colors duration-200"
              >
                Sajiv Jess
              </a>
            </span>
            </div>
          </div>
        </div>
      </div>

      {/* Gold Accent Line */}
      <div className="h-1 bg-gradient-to-r from-transparent via-yellow-600 to-transparent"></div>
    </footer>
  );
};

export default Footer;